import { inject } from '@angular/core';
import { signalStore, withState } from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { Events, withEventHandlers, withReducer } from '@ngrx/signals/events';
import { mapResponse } from '@ngrx/operators';
import { exhaustMap } from 'rxjs';
import { MatchdayWinners, matchdayWinnersEvents, PREDICTION_GAME_SERVICE } from '@kikkz/shared/prediction-game/util';
import { TRANSLATION_SERVICE } from '@kikkz/shared/i18n/util';
import { LOGGING_SERVICE } from '@kikkz/shared/logging/util';
import { serverErrorMessage } from './server-error-message';
import { asyncReducers } from '@kikkz/shared/state/util';

export interface MatchdayWinnersState {
  /** Evaluated fixtures with at least one winner, newest kick-off first (the transport sorts and filters). */
  matchdayWinners: MatchdayWinners[];
  isLoadingMatchdayWinners: boolean;
  matchdayWinnersError: string | null;
}

const initialState: MatchdayWinnersState = {
  matchdayWinners: [],
  isLoadingMatchdayWinners: false,
  matchdayWinnersError: null,
};

/**
 * Matchday-winners region ("Spieltagssieger"): the per-fixture winner list of the prizes
 * tab.
 *
 * A region of its own rather than a corner of the ranking store: it is backed by its own
 * endpoint and fails independently, so a broken winner list must not take the ranking's
 * error field (and with it the ranking view's retry) with it.
 *
 * `exhaustMap` like the other read-only regions — the list has no parameters, so a second
 * dispatch while one request is in flight would only repeat it.
 */
export const MatchdayWinnersStore = signalStore(
  withState(initialState),
  withDevtools('prediction-game-matchday-winners'),
  withReducer(
    ...asyncReducers<MatchdayWinnersState>()({
      load: matchdayWinnersEvents.loadMatchdayWinners,
      success: matchdayWinnersEvents.loadMatchdayWinnersSuccess,
      failed: matchdayWinnersEvents.loadMatchdayWinnersFailed,
      loading: 'isLoadingMatchdayWinners',
      error: 'matchdayWinnersError',
      onSuccess: (state, payload) => {
        state.matchdayWinners = payload.matchdays;
      },
    }),
  ),
  withEventHandlers(
    (
      _,
      events = inject(Events),
      service = inject(PREDICTION_GAME_SERVICE),
      translation = inject(TRANSLATION_SERVICE),
      logger = inject(LOGGING_SERVICE),
    ) => ({
      loadMatchdayWinners$: events.on(matchdayWinnersEvents.loadMatchdayWinners).pipe(
        exhaustMap(() =>
          service.loadMatchdayWinners().pipe(
            mapResponse({
              next: (matchdays) => matchdayWinnersEvents.loadMatchdayWinnersSuccess({ matchdays }),
              error: (error: unknown) =>
                matchdayWinnersEvents.loadMatchdayWinnersFailed({
                  error: serverErrorMessage(error, 'pg.errors.matchdayWinnersLoad', translation, logger),
                }),
            }),
          ),
        ),
      ),
    }),
  ),
);
